import React, { useState, useEffect } from 'react';
import { Box, Text, Alert } from '@mantine/core';
import EntitlementsService from '../../services/EntitlementsService';
import { LoadingState } from './LoadingState';
import { JsonViewer } from './JsonViewer';

export interface Tier2SchemaRawViewProps {
    entityType: string;
}

/**
 * Raw JSON view of tier2 entitlement (GET .../mgmt/Entitlements/entity/{schemaEntityId}):
 * Section 1 = primary response without AttributeBindings/Attributes; Section 2 = AttributeBindings or Attributes.
 */
export const Tier2SchemaRawView: React.FC<Tier2SchemaRawViewProps> = ({ entityType }) => {
    const [tier2, setTier2] = useState<Record<string, unknown> | null | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!entityType?.trim()) {
            setTier2(undefined);
            setLoading(false);
            return;
        }
        const key = entityType.trim().toLowerCase();
        const cached = EntitlementsService.getCachedEntityEntitlementForSchemaEntity(key);
        if (cached != null) {
            setTier2(cached as Record<string, unknown>);
            setLoading(false);
            return;
        }
        setLoading(true);
        EntitlementsService.fetchEntityEntitlementForSchemaEntity(key)
            .then((data) => setTier2((data ?? null) as Record<string, unknown> | null))
            .catch(() => setTier2(null))
            .finally(() => setLoading(false));
    }, [entityType]);

    if (loading) {
        return (
            <LoadingState variant="centered" minHeight={200} label={`Loading tier2 for ${entityType}...`} />
        );
    }

    if (tier2 === null || tier2 === undefined) {
        return (
            <Box p="sm">
                <Alert color="blue">
                    No tier2 data for this entity type. Open the table or Raw &gt; Ent to load it.
                </Alert>
            </Box>
        );
    }

    const { AttributeBindings, Attributes, ...primary } = tier2 as Record<string, unknown>;
    const primaryPayload = Object.keys(primary).length > 0 ? primary : null;
    const bindingsOrAttrs = Array.isArray(AttributeBindings) ? AttributeBindings : (Array.isArray(Attributes) ? Attributes : Attributes ?? []);
    const sectionTitle = Array.isArray(AttributeBindings) ? 'AttributeBindings' : 'Attributes';
    const sectionDescription = Array.isArray(AttributeBindings)
        ? 'Attribute list with Binding (Required, RegEx, AllowedValues) per attribute.'
        : 'Attribute list for this entity type (legacy shape).';

    return (
        <Box p="sm">
            <Text size="sm" c="dimmed" mb="sm">
                GET .../mgmt/Entitlements/entity/&#123;schemaEntityId&#125;. Same data as Table view, in raw JSON.
            </Text>
            {primaryPayload != null && (
                <JsonViewer
                    data={primaryPayload}
                    title={`Primary (without ${sectionTitle})`}
                    description="Top-level fields from the entitlement response (e.g. Entity, CreateEntity, EditEntity, IsAdministrator)."
                />
            )}
            <Box mt={primaryPayload != null ? 'md' : 0}>
                <JsonViewer
                    data={bindingsOrAttrs}
                    title={sectionTitle}
                    description={sectionDescription}
                />
            </Box>
        </Box>
    );
};
